import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, ArrowRight, Mic, Sparkles, RefreshCw, ChevronRight, Trophy, AlertCircle } from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Seo from '@/components/shared/Seo';
import { ieltsApi, goToIeltsAuth } from '@/lib/ieltsApi';
import { toast } from 'sonner';
import WinhouseAIFeedbackBadge from '@/components/shared/WinhouseAIFeedbackBadge';

const fmtDate = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; } };
const PART_LABEL = { part_1: 'Part 1', part_2: 'Part 2', part_3: 'Part 3' };
const AREA_LABEL = { fluency_coherence: 'Fluency & Coherence', lexical_resource: 'Lexical Resource', grammatical_range_accuracy: 'Grammar', pronunciation: 'Pronunciation' };

const Detail = ({ sid, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [evaluating, setEvaluating] = useState(false);
  const load = async () => { setLoading(true); try { setData(await ieltsApi.speakingGetSubmission(sid)); } catch (e) { toast.error(e.message); } finally { setLoading(false); } };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sid]);
  const evaluate = async () => { setEvaluating(true); try { const r = await ieltsApi.speakingEvaluateSubmission(sid); toast.success(`Band: ${r.estimated_band_overall ?? '—'}`); await load(); } catch (e) { toast.error(e.message); } finally { setEvaluating(false); } };
  if (loading) return <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  if (!data) return null;
  const ai = data.ai_feedback || {}; const bands = ai.bands || {};
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7" data-testid="speaking-submission-detail">
      <div className="flex items-center justify-between mb-3">
        <Button variant="ghost" size="sm" onClick={onClose} className="rounded-full"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        <div className="flex items-center gap-2">
          {ai.bands && <WinhouseAIFeedbackBadge model={ai.model || 'claude-sonnet-4-5'} />}
          {!ai.bands && (<Button onClick={evaluate} disabled={evaluating} className="bg-black text-white rounded-full">{evaluating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}Get AI Feedback</Button>)}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Badge className="bg-black text-white">{PART_LABEL[data.speaking_part]}</Badge>
        <Badge variant="outline" className="capitalize">{data.topic}</Badge>
        <span className="text-xs text-gray-500">{fmtDate(data.submitted_at)} · {data.word_count} words</span>
      </div>
      {ai.bands && (
        <div className="grid sm:grid-cols-5 gap-2 mb-4">
          <div className="bg-black text-white rounded-xl p-3"><div className="text-[10px] uppercase tracking-wider text-white/70">Overall</div><div className="text-2xl font-bold text-[#D3D86F]">{data.estimated_band_overall ?? '—'}</div></div>
          <div className="bg-gray-50 border rounded-xl p-3"><div className="text-[10px] uppercase text-gray-500">F&amp;C</div><div className="text-xl font-bold">{bands.fluency_coherence ?? '—'}</div></div>
          <div className="bg-gray-50 border rounded-xl p-3"><div className="text-[10px] uppercase text-gray-500">Lexical</div><div className="text-xl font-bold">{bands.lexical_resource ?? '—'}</div></div>
          <div className="bg-gray-50 border rounded-xl p-3"><div className="text-[10px] uppercase text-gray-500">Grammar</div><div className="text-xl font-bold">{bands.grammatical_range_accuracy ?? '—'}</div></div>
          <div className="bg-gray-50 border rounded-xl p-3"><div className="text-[10px] uppercase text-gray-500">Pronunciation*</div><div className="text-xl font-bold">{bands.pronunciation ?? '—'}</div></div>
        </div>
      )}
      {ai.pronunciation_note && <div className="text-xs text-gray-500 italic mb-3">*{ai.pronunciation_note}</div>}
      {ai.summary && <p className="text-sm bg-gray-50 border rounded-xl p-3 leading-7 mb-3">{ai.summary}</p>}
      <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Your transcript</h4>
      <div className="bg-gray-50 border rounded-xl p-4 whitespace-pre-wrap font-serif text-[14px] leading-7 max-h-72 overflow-auto" data-testid="speaking-submission-text">{data.response_text}</div>
      {ai.improved_sample_answer && (
        <div className="mt-4 bg-black text-white rounded-2xl p-4">
          <div className="text-xs uppercase tracking-wider text-[#D3D86F] font-semibold mb-2">Band 8 sample answer</div>
          <div className="text-sm leading-7 font-serif text-white/90 whitespace-pre-wrap">{ai.improved_sample_answer}</div>
        </div>
      )}
      {ai.bands && <WinhouseAIFeedbackBadge variant="footer" model={ai.model || 'claude-sonnet-4-5'} />}
    </div>
  );
};

const IeltsSpeakingDashboardPage = () => {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [d, l] = await Promise.all([ieltsApi.speakingDashboard(), ieltsApi.speakingListSubmissions(30)]);
      setStats(d); setSubmissions(l.submissions || []);
    } catch (e) { toast.error(e.message || 'Failed to load dashboard'); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    let mounted = true;
    (async () => {
      try { await ieltsApi.authMe(); if (!mounted) return; setAuthChecked(true); refresh(); }
      catch { if (!mounted) return; setAuthChecked(true); setNeedsLogin(true); }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  if (!authChecked) return (<div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-red-700" /></div>);
  if (needsLogin) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-md text-center bg-white border border-gray-200 rounded-2xl p-8">
            <h1 className="text-2xl font-bold mb-2">Sign in to view your dashboard</h1>
            <Button onClick={() => goToIeltsAuth('/ielts/speaking/dashboard')} className="bg-black text-white rounded-full px-6 h-11">Sign in with Google</Button>
          </div>
        </main>
      </div>
    );
  }

  const counts = stats?.prompt_counts || {};
  const avg = stats?.band_breakdown_avg || {};
  const weakest = stats?.weakest_area;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="ielts-speaking-dashboard">
      <Seo title="Speaking Dashboard · Winhouse IELTS" canonical="/ielts/speaking/dashboard" />
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-12 w-full">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <Link to="/ielts" className="text-sm text-gray-500 hover:text-black inline-flex items-center gap-1 mb-2"><ArrowLeft className="h-3.5 w-3.5" /> IELTS Hub</Link>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Speaking Dashboard</h1>
            <p className="text-gray-600 text-sm mt-1">Track your band progress across all three Speaking parts.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={refresh} variant="outline" className="rounded-full"><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>
            <Button onClick={() => navigate('/ielts/speaking/practice')} className="bg-black text-white rounded-full">Practise <ArrowRight className="h-4 w-4 ml-1" /></Button>
            <Button onClick={() => navigate('/ielts/speaking/mock')} className="bg-[#D3D86F] text-black hover:bg-[#c4ca5e] rounded-full">Mock Test</Button>
          </div>
        </div>

        {loading ? (<div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>)
          : selected ? (<Detail sid={selected} onClose={() => setSelected(null)} />)
          : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-black text-white rounded-2xl p-5">
                  <div className="text-xs uppercase tracking-wider text-white/60 mb-1">Total responses</div>
                  <div className="text-4xl font-bold text-[#D3D86F]">{stats?.total_attempts ?? 0}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-5"><div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Avg Fluency</div><div className="text-3xl font-bold">{avg.fluency_coherence ?? '—'}</div></div>
                <div className="bg-white border border-gray-200 rounded-2xl p-5"><div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Avg Lexical</div><div className="text-3xl font-bold">{avg.lexical_resource ?? '—'}</div></div>
                <div className="bg-white border border-gray-200 rounded-2xl p-5"><div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Avg Grammar</div><div className="text-3xl font-bold">{avg.grammatical_range_accuracy ?? '—'}</div></div>
              </div>

              {weakest && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-center gap-3 text-sm" data-testid="speaking-weakest-banner">
                  <AlertCircle className="h-5 w-5 text-amber-700" />
                  <span><strong className="font-semibold">Focus area: </strong> Your weakest descriptor is <strong>{AREA_LABEL[weakest] || weakest}</strong>. Practise targeted prompts to lift it.</span>
                </div>
              )}

              <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6">
                <h3 className="font-bold text-base mb-3 flex items-center gap-2"><Mic className="h-4 w-4 text-red-700" /> Prompt library</h3>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="border rounded-xl p-3"><div className="text-xs text-gray-500">Part 1</div><div className="text-2xl font-bold">{counts.part_1 ?? 0}</div></div>
                  <div className="border rounded-xl p-3"><div className="text-xs text-gray-500">Part 2</div><div className="text-2xl font-bold">{counts.part_2 ?? 0}</div></div>
                  <div className="border rounded-xl p-3"><div className="text-xs text-gray-500">Part 3</div><div className="text-2xl font-bold">{counts.part_3 ?? 0}</div></div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="speaking-submissions-list">
                <div className="px-5 py-4 border-b flex items-center justify-between">
                  <h3 className="font-bold text-base flex items-center gap-2"><Trophy className="h-4 w-4 text-red-700" /> Recent responses</h3>
                  <span className="text-xs text-gray-500">{submissions.length} latest</span>
                </div>
                {submissions.length === 0 ? (
                  <div className="p-10 text-center text-gray-500 text-sm">No responses yet — <button onClick={() => navigate('/ielts/speaking/practice')} className="text-red-700 underline">start a practice prompt</button>.</div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {submissions.map((s) => (
                      <li key={s.submission_id} className="px-5 py-4 hover:bg-gray-50 cursor-pointer flex flex-wrap items-center gap-3 justify-between" onClick={() => setSelected(s.submission_id)}>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                            <Badge className="bg-black text-white text-[10px] py-0.5">{PART_LABEL[s.speaking_part]}</Badge>
                            <Badge variant="outline" className="capitalize text-[10px] py-0.5">{s.topic}</Badge>
                            <span className="text-xs text-gray-500">{fmtDate(s.submitted_at)}</span>
                          </div>
                          <div className="text-sm text-gray-700 truncate max-w-md">{s.word_count} words {s.has_recording ? '· 🎙 recorded' : ''}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right"><div className="text-[10px] uppercase tracking-wider text-gray-400">Band</div><div className="text-xl font-bold tabular-nums">{s.estimated_band_overall ?? '—'}</div></div>
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
      </main>
      <Footer />
    </div>
  );
};
export default IeltsSpeakingDashboardPage;
